/**
 * Step 7: Risk Intelligence - Final step showing risk score
 *
 * Resting-state fixes (2026-07-21 sweep, second occurrence PROC-000042 -
 * PROC-000038 was the first): a procurement resumed at status
 * "rfp_generated" lands directly on this step via getStepFromStatus
 * (WizardContext.jsx) WITHOUT going through handleProceedToRisk - that
 * handler (the only place that calls POST /assess-risk) is only invoked by
 * StepRfpPreview's "Generate Risk Intelligence" button, i.e. the forward-
 * navigation path. The resume-jump path never calls it, so riskAssessment
 * stays null and RiskScoreCard (which returns null for a falsy
 * riskAssessment) silently rendered an empty white box, with the
 * calibration banner and an always-enabled Complete button giving no
 * indication anything was wrong or actionable.
 *
 * Rather than adding an auto-fire effect on mount (which would silently
 * spend a generation attempt the user never asked for, and would need its
 * own re-entrancy guarding), this step now owns its own resting state
 * unconditionally, regardless of which path landed the user here - never
 * blank: riskAssessment present -> the card; absent + a generate attempt
 * failed this session -> inline error + Retry; absent + no attempt yet
 * (fresh resume, or forward nav that somehow didn't fire) -> an explicit
 * "Generate Risk Intelligence" action (terminology fix 2026-07-21: the
 * product name is "Risk Intelligence" - page title, StepRfpPreview's own
 * button - the resting-state UI below originally said "Risk Assessment",
 * now aligned). This also transitively fixes the
 * broken resume path without touching the navigation/status-mapping logic
 * itself - the step is now correct no matter how you arrive at it.
 */
import { useState } from 'react';
import { useWizard } from './WizardContext';
import GenerationLock from './GenerationLock';
import { RegeneratedNotice } from './WizardBanners';
import { RiskScoreCard, RiskBadge, RiskAttestationMarker } from '@/components/RiskScoreCard';
import RfpFeedbackDialog from '@/components/RfpFeedbackDialog';
import { Button } from '@/components/ui/button';
import { ArrowLeft, CheckCircle, AlertTriangle, Info, MessageSquare, CheckCircle2, ShieldAlert, Loader2 } from 'lucide-react';

const StepRisk = () => {
  const {
    loading,
    riskAssessment,
    procurementId,
    refinedRequirement,
    setCurrentStep,
    handleProceedToRisk,
    handleComplete,
    wizardHeaderHeight,
    rfpVersion,
    feedbackSubmittedVersion,
    setFeedbackSubmittedVersion,
    regeneratedNotice
  } = useWizard();

  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  // Failed-this-session tracking (resting-state fix 3) - local, not in
  // WizardContext: only meaningful for a generate/retry attempt initiated
  // from THIS step's own button, resets naturally on remount (fresh
  // navigation away and back starts clean, matching every other step's
  // local-state convention in this wizard).
  const [riskAttemptFailed, setRiskAttemptFailed] = useState(false);

  const handleGenerateClick = async () => {
    const succeeded = await handleProceedToRisk();
    // null = the risk-stage fork opened (a question, not a failure): leave
    // the resting state as it was rather than showing "failed to generate"
    // behind the dialog.
    if (succeeded !== null) setRiskAttemptFailed(!succeeded);
  };

  // Reaching this step with a riskAssessment means assess-risk already
  // succeeded, and that same backend call sets the procurement's status to
  // "completed" - riskAssessment being present is therefore equivalent to
  // status === "completed" here.
  const isCompleted = Boolean(riskAssessment);
  const category = refinedRequirement?.buyer_input?.category || refinedRequirement?.buyer_input?.l1_category || 'General';
  const { risk_score, risk_level } = riskAssessment || {};

  // Version-aware (RFP can regenerate to a new version under P2): feedback
  // only reads as "received" when it was given for the CURRENT rfp_version.
  const feedbackIsCurrent = feedbackSubmittedVersion != null && feedbackSubmittedVersion === rfpVersion;
  const feedbackWasGivenOnOlderVersion = feedbackSubmittedVersion != null && !feedbackIsCurrent;

  return (
    <div className="relative">
      <GenerationLock active={loading} />

      {/* Sticky sub-header: title + subtitle (left), score + badge (right).
          Pins flush below the wizard strip (WizardHeader) - top offset is
          the logo-bar base (matches BELOW_LOGO_BAR_STICKY: top-20 mobile /
          top-14 desktop) plus WizardHeader's own measured height, so there's
          zero pre-pin travel the same way every other sticky row in this
          app works. -mx-6/md:-mx-8 + matching px cancels the wizard card's
          own p-6 md:p-8 so this row's border reaches the card edges. */}
      <div
        className="sticky z-20 bg-white border-b border-zinc-200 -mx-6 md:-mx-8 px-6 md:px-8 py-4 mb-8 flex items-center justify-between gap-4 flex-wrap top-[calc(5rem_+_var(--wh))] lg:top-[calc(3.5rem_+_var(--wh))]"
        style={{ '--wh': `${wizardHeaderHeight}px` }}
      >
        <div>
          <h2 className="text-2xl font-bold text-zinc-900 mb-1">
            Risk Intelligence
          </h2>
          <p className="text-zinc-600 text-sm">
            Indicative risk profile of the goods/services being procured, scored against your risk domains.
          </p>
        </div>
        {riskAssessment && (
          <div className="flex items-center gap-3 shrink-0">
            <span className="text-2xl font-bold text-zinc-900 tabular-nums">
              {risk_score !== undefined ? `${Math.round(risk_score)}/100` : 'N/A'}
            </span>
            <RiskBadge level={risk_level} />
            <RiskAttestationMarker riskAssessment={riskAssessment} compact />
          </div>
        )}
      </div>

      {regeneratedNotice?.stepId === 'risk' && (
        <RegeneratedNotice causeLabel={regeneratedNotice.causeLabel} />
      )}

      <div className="bg-white border border-zinc-200 p-8 mb-6">
        {riskAssessment ? (
          <RiskScoreCard riskAssessment={riskAssessment} showScoreHeader={false} />
        ) : riskAttemptFailed ? (
          <div className="text-center py-8" data-testid="risk-assessment-failed-state">
            <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-3" />
            <p className="text-sm text-zinc-700 mb-4">Risk Intelligence failed to generate. Please try again.</p>
            <Button
              onClick={handleGenerateClick}
              disabled={loading}
              className="bg-[#0055FF] hover:bg-[#0044CC] text-white"
              data-testid="retry-risk-assessment-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Retry
            </Button>
          </div>
        ) : (
          <div className="text-center py-8" data-testid="risk-assessment-absent-state">
            <ShieldAlert className="w-8 h-8 text-zinc-400 mx-auto mb-3" />
            <p className="text-sm text-zinc-600 mb-4">Risk Intelligence has not been generated yet.</p>
            <Button
              onClick={handleGenerateClick}
              disabled={loading}
              className="bg-[#0055FF] hover:bg-[#0044CC] text-white"
              data-testid="generate-risk-assessment-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Generate Risk Intelligence
            </Button>
          </div>
        )}
      </div>

      <RfpFeedbackDialog
        open={showFeedbackDialog}
        onClose={() => setShowFeedbackDialog(false)}
        procurementId={procurementId}
        category={category}
        onSubmitComplete={(submittedForVersion) => {
          if (submittedForVersion != null) {
            setFeedbackSubmittedVersion(submittedForVersion);
          }
          setShowFeedbackDialog(false);
        }}
      />

      {/* Important Note about Risk Ratings - only meaningful once there's an
          actual assessment to calibrate against (resting-state fix 2). */}
      {riskAssessment && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-8">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-amber-900 mb-1">Important: Risk Calibration Required</h4>
              <p className="text-sm text-amber-800 leading-relaxed">
                These risk ratings are based on the <strong>current procurement details</strong> and the <strong>category&apos;s inherent risks</strong>.
                Before finalizing, please review and calibrate these risk scores considering:
              </p>
              <ul className="mt-2 space-y-1 text-sm text-amber-800">
                <li className="flex items-start gap-2">
                  <span className="text-amber-600 mt-1">•</span>
                  <span>The <strong>final intended use</strong> of the procured goods/services in your organization</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-600 mt-1">•</span>
                  <span>Your <strong>past experience</strong> with the vendor (if any)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-600 mt-1">•</span>
                  <span>Any <strong>specific compliance or regulatory</strong> requirements for your use case</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Guidance Note - same gating as above (resting-state fix 2). */}
      {riskAssessment && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-blue-800">
              Risk Intelligence is a guidance tool. The final risk evaluation should be performed by your procurement
              and compliance teams based on organizational policies and the specific context of this procurement.
            </p>
          </div>
        </div>
      )}

      <div className="flex justify-between items-start">
        <Button variant="outline" onClick={() => setCurrentStep(5)} disabled={loading} className="border-zinc-200">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        <div className="flex flex-col items-end gap-1">
          <Button
            onClick={handleComplete}
            disabled={!riskAssessment}
            className="bg-green-600 hover:bg-green-700 text-white px-6 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="complete-procurement-btn"
          >
            <CheckCircle className="w-4 h-4 mr-2" /> Complete
          </Button>
          {/* Resting-state fix 1: a disabled Complete must always state why,
              never a silent no-op. */}
          {!riskAssessment && (
            <span className="text-xs text-zinc-500" data-testid="complete-disabled-reason">
              Risk Intelligence has not been generated yet
            </span>
          )}
        </div>
      </div>

      {/* Feedback invitation, kept at the end of the step's content */}
      {isCompleted && (
        feedbackIsCurrent ? (
          <p className="mt-8 text-center text-sm text-zinc-500" data-testid="feedback-received-note">
            <CheckCircle2 className="w-4 h-4 inline-block mr-1.5 text-green-500 align-text-bottom" />
            Feedback received
          </p>
        ) : (
          <div className="mt-8 border border-zinc-200 bg-zinc-50 rounded-lg p-4 text-center">
            <p className="text-sm text-zinc-700 mb-3">
              {feedbackWasGivenOnOlderVersion
                ? 'The RFP has been regenerated since your last feedback — let us know what you think of the updated version.'
                : "You've now seen the complete RFP and risk profile — if you have any feedback, we'd love to hear it."}
            </p>
            <Button
              onClick={() => setShowFeedbackDialog(true)}
              className="bg-[#0055FF] hover:bg-[#0044CC] text-white"
              data-testid="send-feedback-btn"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              {feedbackWasGivenOnOlderVersion ? 'Share feedback on the updated RFP' : 'Share feedback on this procurement'}
            </Button>
          </div>
        )
      )}
    </div>
  );
};

export default StepRisk;
